<div class="spinner-container" *ngIf="isLoadingMetadata || isLoadingOptions">
  <div class="spinner"></div>
</div>

<div class="metadata-page" *ngIf="!isLoadingMetadata && !isLoadingOptions">
  <div class="card" *ngIf="metadataLoadFailed">
    <div class="card-content">
      <div class="value-box missing-data">
        <lucide-angular name="circle-x" [size]="20" class="card-icon card-icon-red"></lucide-angular>
        <span>{{ "metadataPage.error.metadataLoad" | translate }}</span>
      </div>
    </div>
  </div>

  <form *ngIf="metadataForm" [formGroup]="metadataForm">
    <!-- Previously loaded metadata may be out of date when reloading it failed, so it isn't displayed then -->
    <ng-container *ngIf="sampleMetadata && !metadataLoadFailed">
      <!-- Header -->
      <div class="card">
        <div class="page-header">
          <div class="header-left">
            <a [routerLink]="['/home/samples']" class="nav-link">
              <lucide-angular name="arrow-left" [size]="18" class="card-icon card-icon-gray"></lucide-angular>
            </a>

            <div class="page-header-text">
              <h2 class="page-title">{{ "metadataPage.title" | translate }}</h2>
              <div class="label">
                {{ "metadataPage.sampleID" | translate }}:
                <span class="highlight">{{ sampleMetadata.metadata.sampleID }}</span>
              </div>
            </div>
          </div>

          <div class="header-right">
            <ng-container *ngIf="!editMode; else edit">
              <button
                *ngIf="canPredict"
                [disabled]="isLoadingCheck || checkLoadFailed || !check.ready || (check.ready && check.prediction)"
                [attr.title]="
                  isLoadingCheck
                    ? null
                    : checkLoadFailed
                      ? ('metadataPage.prediction.checkUnavailable' | translate)
                      : (check | predictionTooltip | translate)
                "
                class="button-blue"
                type="button"
                (click)="predict()"
              >
                <lucide-angular name="play" [size]="18"></lucide-angular>
                {{ "button.predict" | translate }}
              </button>

              <button *ngIf="canEdit" class="button-gray" type="button" (click)="toggleEdit()">
                <lucide-angular name="square-pen" [size]="18"></lucide-angular>
                {{ "button.edit" | translate }}
              </button>
            </ng-container>

            <ng-template #edit>
              <button class="button-blue" type="button" (click)="cancelEdit()" [disabled]="metadataForm.disabled">
                <lucide-angular name="circle-x" [size]="18"></lucide-angular>
                {{ "button.cancel" | translate }}
              </button>

              <button class="button-gray" type="button" (click)="saveEdit()" [disabled]="metadataForm.disabled">
                <lucide-angular name="save" [size]="18"></lucide-angular>
                {{ "button.save" | translate }}
              </button>
            </ng-template>
          </div>
        </div>
      </div>

      <!-- Only the prediction check area waits for the check, e.g. while it is reloaded after a save -->
      <div class="card" *ngIf="isLoadingCheck">
        <div class="spinner-container">
          <div class="spinner spinner-small"></div>
        </div>
      </div>

      <!-- A previous check may no longer match the saved metadata, so none is displayed -->
      <div class="card" *ngIf="!isLoadingCheck && checkLoadFailed">
        <div class="card-content">
          <div class="value-box missing-data">
            <lucide-angular name="circle-x" [size]="20" class="card-icon card-icon-red"></lucide-angular>
            <span>{{ "metadataPage.prediction.checkUnavailable" | translate }}</span>
          </div>
        </div>
      </div>

      <ng-container *ngIf="!isLoadingCheck && !checkLoadFailed">
        <!-- MISSING DATA -->
        <div class="card" *ngIf="(check.missing?.length ?? 0) > 0">
          <div class="card-header">
            <h3 class="card-header-left header-title">
              {{ "metadataPage.prediction.missingData.title" | translate }}
            </h3>

            <button type="button" class="card-header-right close-btn" (click)="showMissing = !showMissing">
              <lucide-angular [name]="showMissing ? 'chevron-up' : 'chevron-down'" [size]="18"></lucide-angular>
            </button>
          </div>

          <div *ngIf="showMissing" class="card-content">
            <div class="missing-data-grid">
              <div *ngFor="let data of check.missing" class="value-box missing-data">
                <lucide-angular name="circle-x" [size]="20" class="card-icon card-icon-red"></lucide-angular>
                <span>{{ data | predictionMissingData | translate }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- PREDICTION -->
        <div class="card" *ngIf="check.prediction">
          <h3 class="card-header header-title">{{ "metadataPage.prediction.sensitivityPrediction" | translate }}</h3>

          <div class="card-content">
            <div class="scale">
              <span>{{ "metadataPage.prediction.resistant" | translate }}</span>
              <div class="bar-bg">
                <div class="bar-fill" [style.width.%]="check.prediction.sensitivityProbability * 100"></div>
              </div>
              <span>{{ "metadataPage.prediction.sensitive" | translate }}</span>
            </div>

            <div class="prediction">
              <div class="value-box">
                <span>{{ "metadataPage.prediction.probability" | translate }}</span>
                <strong>{{ check.prediction.sensitivityProbability }}</strong>
              </div>
              <div class="value-box">
                <span>{{ "metadataPage.prediction.threshold" | translate }}</span>
                <strong>{{ threshold }}</strong>
              </div>
              <div class="value-box" *ngIf="check.prediction.model">
                <span>{{ "metadataPage.prediction.model.title" | translate }}</span>
                <strong>{{ "metadataPage.prediction.model." + check.prediction.model | translate }}</strong>
              </div>
            </div>
          </div>
        </div>

        <!-- SHAP -->
        <div class="card" *ngIf="check.prediction">
          <div class="card-header">
            <h3 class="card-header-left header-title">
              {{ "metadataPage.prediction.shap.title" | translate }}
            </h3>

            <button type="button" class="card-header-right close-btn" (click)="showShap = !showShap">
              <lucide-angular [name]="showShap ? 'chevron-up' : 'chevron-down'" [size]="18"></lucide-angular>
            </button>
          </div>

          <div *ngIf="showShap" class="card-content">
            <app-shap-details [baseValue]="check.prediction.baseValue" [shapValues]="check.prediction.shapValues"></app-shap-details>
          </div>
        </div>
      </ng-container>

      <div class="two-card">
        <!-- PERSONAL DATA -->
        <div class="card" formGroupName="metadata">
          <div class="card-header">
            <div class="card-header-left">
              <lucide-angular name="user" [size]="20" class="card-icon card-icon-blue"></lucide-angular>
              <h3 class="header-title">{{ "metadataPage.metadata.header" | translate }}</h3>
            </div>
          </div>

          <div class="card-content">
            <div class="two-field-row">
              <app-single-field
                formControlName="name"
                [label]="'common.name' | translate"
                [displayValue]="sampleMetadata.metadata.name || '-'"
                [editMode]="editMode"
                [isInput]="true"
                [maxlength]="100"
              ></app-single-field>

              <app-single-field
                formControlName="firstname"
                [label]="'common.firstname' | translate"
                [displayValue]="sampleMetadata.metadata.firstname || '-'"
                [editMode]="editMode"
                [isInput]="true"
                [maxlength]="100"
              ></app-single-field>
            </div>

            <div class="two-field-row">
              <!-- Not modifiable through the API, so kept read-only in edit mode -->
              <app-single-field
                [label]="'metadataPage.sampleID' | translate"
                [displayValue]="sampleMetadata.metadata.sampleID"
                [editMode]="false"
              ></app-single-field>

              <ng-container formGroupName="demographic">
                <app-single-field
                  formControlName="age"
                  [label]="'metadataPage.metadata.demographic.age' | translate"
                  [displayValue]="sampleMetadata.metadata.demographic.age"
                  [suffix]="'metadataPage.metadata.years' | translate | lowercase"
                  [editMode]="editMode"
                  [isInput]="true"
                  [type]="'number'"
                  [min]="0"
                  [required]="true"
                  [requiredError]="metadataForm.controls.metadata.controls.demographic.controls.age.errors?.['required']"
                ></app-single-field>
              </ng-container>
            </div>

            <!-- Not modifiable through the API, so kept read-only in edit mode -->
            <div class="two-field-row">
              <app-single-field
                [label]="'metadataPage.metadata.biobank' | translate"
                [displayValue]="sampleMetadata.metadata.biobank.name + ' (' + sampleMetadata.metadata.biobank.acronym + ')'"
                [editMode]="false"
              ></app-single-field>

              <app-single-field
                [label]="'metadataPage.metadata.cancerType' | translate"
                [displayValue]="sampleMetadata.metadata.cancerType.name + ' (' + sampleMetadata.metadata.cancerType.acronym + ')'"
                [editMode]="false"
              ></app-single-field>
            </div>

            <div class="two-field-row" formGroupName="demographic">
              <app-single-field
                formControlName="sex"
                [label]="'metadataPage.metadata.demographic.sex.title' | translate"
                [displayValue]="
                  'metadataPage.metadata.demographic.sex.' + (sampleMetadata.metadata.demographic.sex | lowercase) | translate
                "
                [editMode]="editMode"
                [isInput]="false"
                [options]="sexOptions"
                [required]="true"
                [requiredError]="metadataForm.controls.metadata.controls.demographic.controls.sex.errors?.['required']"
              ></app-single-field>

              <app-single-field
                formControlName="bmi"
                [label]="'metadataPage.metadata.demographic.bmi' | translate"
                [displayValue]="sampleMetadata.metadata.demographic.bmi | number: '1.2-2'"
                [suffix]="'kg/m²'"
                [editMode]="editMode"
                [isInput]="true"
                [type]="'number'"
                [min]="0"
                [required]="bmiRequired"
                [requiredError]="metadataForm.controls.metadata.controls.demographic.controls.bmi.errors?.['required']"
              ></app-single-field>
            </div>

            <div class="two-field-row">
              <ng-container formGroupName="demographic">
                <app-single-field
                  formControlName="smokeTobacco"
                  [label]="'metadataPage.metadata.demographic.smokeTobacco' | translate"
                  [displayValue]="sampleMetadata.metadata.demographic.smokeTobacco | booleanLabel | translate"
                  [editMode]="editMode"
                  [isInput]="false"
                  [options]="getBooleanOptions(sampleMetadata.metadata.demographic.smokeTobacco)"
                ></app-single-field>
              </ng-container>

              <div class="field" formGroupName="medical">
                <div class="label">{{ "metadataPage.metadata.diabetes" | translate }}</div>
                <ng-container *ngIf="!editMode; else editDiabetes">
                  <div class="value-box">
                    {{ sampleMetadata.metadata.medical.diabetes | booleanLabel | translate }}

                    <ng-container *ngIf="sampleMetadata.metadata.medical.diabetes">
                      -
                      {{
                        sampleMetadata.metadata.medical.diabetesType === "UNKNOWN"
                          ? ("common.unknown" | translate)
                          : (sampleMetadata.metadata.medical.diabetesType | titlecase)
                      }}
                    </ng-container>
                  </div>
                </ng-container>

                <ng-template #editDiabetes>
                  <div class="field-edit">
                    <select class="value-box" formControlName="diabetesType">
                      <option *ngFor="let opt of getDiabetesOptions(sampleMetadata.metadata.medical.diabetes)" [ngValue]="opt.value">
                        {{ opt.translate1 ? (opt.label1 | translate) : opt.label1 }}

                        <ng-container *ngIf="opt.label2">
                          -
                          {{ opt.translate2 ? (opt.label2 | translate) : opt.label2 }}
                        </ng-container>
                      </option>
                    </select>

                    <div
                      class="error-message"
                      *ngIf="metadataForm.controls.metadata.controls.medical.controls.diabetesType.errors?.['required']"
                    >
                      <span>{{ "common.required" | translate }} </span>
                    </div>
                  </div>
                </ng-template>
              </div>
            </div>

            <div class="field" formGroupName="medical">
              <div class="label">
                {{ "metadataPage.metadata.medical.title" | translate }}
              </div>

              <div class="value-box">
                <ul>
                  <li *ngFor="let field of medicalFields">
                    <app-multiple-fields
                      [formControlName]="field"
                      [label]="'metadataPage.metadata.medical.' + field | translate"
                      [editMode]="editMode"
                      [isInput]="false"
                      [options]="getBooleanOptions(sampleMetadata.metadata.medical[field])"
                    >
                      {{ sampleMetadata.metadata.medical[field] | booleanLabel | translate }}
                    </app-multiple-fields>
                  </li>
                </ul>
              </div>
            </div>

            <div class="field" formGroupName="symptomatologyAfterSurgery">
              <div class="label">
                {{ "metadataPage.metadata.symptomatologyAfterSurgery.title" | translate }}
              </div>

              <div class="value-box">
                <ul class="value-grid-two">
                  <li *ngFor="let field of symptomatologyAfterSurgeryFields">
                    <app-multiple-fields
                      [formControlName]="field"
                      [label]="'metadataPage.metadata.symptomatologyAfterSurgery.' + field | translate"
                      [editMode]="editMode"
                      [isInput]="false"
                      [options]="getBooleanOptions(sampleMetadata.metadata.symptomatologyAfterSurgery[field])"
                    >
                      {{ sampleMetadata.metadata.symptomatologyAfterSurgery[field] | booleanLabel | translate }}
                    </app-multiple-fields>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- TUMOUR DATA -->
        <div class="card" formGroupName="tumor">
          <div class="card-header">
            <div class="card-header-left">
              <lucide-angular name="activity" [size]="20" class="card-icon card-icon-red"></lucide-angular>
              <h3 class="header-title">{{ "metadataPage.tumor.header" | translate }}</h3>
            </div>
          </div>
          <div class="card-content">
            <div class="two-field-row">
              <app-single-field
                formControlName="mrnaSeqID"
                [label]="'metadataPage.tumor.mRNASeqID' | translate"
                [displayValue]="sampleMetadata.tumor.mrnaSeqID || '-'"
                [editMode]="editMode"
                [isInput]="true"
              ></app-single-field>

              <ng-container formGroupName="tumorCharacteristicsAndHistology">
                <app-single-field
                  formControlName="location"
                  [label]="'metadataPage.tumor.location' | translate"
                  [displayValue]="sampleMetadata.tumor.tumorCharacteristicsAndHistology.location || '-'"
                  [editMode]="editMode"
                  [isInput]="false"
                  [options]="locationOptions"
                  [required]="true"
                  [requiredError]="
                    metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls.location.errors?.['required']
                  "
                ></app-single-field>
              </ng-container>
            </div>

            <div class="two-field-row">
              <app-single-field
                formControlName="sampleType"
                [label]="'metadataPage.tumor.sampleType' | translate"
                [displayValue]="sampleMetadata.tumor.sampleType || '-'"
                [editMode]="editMode"
                [isInput]="true"
                [required]="true"
                [requiredError]="metadataForm.controls.tumor.controls.sampleType.errors?.['required']"
              ></app-single-field>

              <app-single-field
                formControlName="differentiationDegree"
                [label]="'metadataPage.tumor.differentiationDegree' | translate"
                [displayValue]="sampleMetadata.tumor.differentiationDegree"
                [prefix]="'metadataPage.tumor.grade' | translate"
                [editMode]="editMode"
                [isInput]="true"
                [type]="'number'"
                [min]="0"
                [required]="differentiationDegreeRequired"
                [requiredError]="metadataForm.controls.tumor.controls.differentiationDegree.errors?.['required']"
              ></app-single-field>
            </div>

            <div class="field" formGroupName="tumorCharacteristicsAndHistology">
              <div class="label">{{ "metadataPage.tumor.tumorCharacteristicsAndHistology.title" | translate }}</div>

              <div class="value-box">
                <ul class="value-grid-three">
                  <li *ngFor="let field of histologyFields">
                    {{ "metadataPage.tumor.tumorCharacteristicsAndHistology." + field.key | translate }}
                    <ng-container *ngIf="editMode && field.required">*</ng-container>:

                    <ng-container *ngIf="!editMode; else editField">
                      <ng-container *ngIf="field.type === 'boolean'; else textValue">
                        {{ $any(sampleMetadata.tumor.tumorCharacteristicsAndHistology)[field.key] | booleanLabel | translate }}
                      </ng-container>

                      <ng-template #textValue>
                        <ng-container *ngIf="field.type === 'select'; else freeTextValue">
                          {{ histologyOption.translate ? (histologyOption.label | translate) : histologyOption.label || "-" }}
                        </ng-container>
                      </ng-template>

                      <ng-template #freeTextValue>
                        {{ sampleMetadata.tumor.tumorCharacteristicsAndHistology[field.key] || "-" }}
                      </ng-template>
                    </ng-container>

                    <ng-template #editField>
                      <div>
                        <ng-container *ngIf="field.type === 'boolean'; else selectOrTextInput">
                          <select class="value-box no-background" [formControlName]="field.key">
                            <option
                              *ngFor="let opt of getBooleanOptions(sampleMetadata.tumor.tumorCharacteristicsAndHistology[field.key])"
                              [ngValue]="opt.value"
                            >
                              {{ opt.translate ? (opt.label | translate) : opt.label }}
                            </option>
                          </select>
                        </ng-container>

                        <ng-template #selectOrTextInput>
                          <select
                            *ngIf="field.type === 'select'; else textInput"
                            class="value-box no-background"
                            [formControlName]="field.key"
                          >
                            <option *ngFor="let opt of histologyOptions" [ngValue]="opt.value">
                              {{ opt.translate ? (opt.label | translate) : opt.label }}
                            </option>
                          </select>

                          <ng-template #textInput>
                            <input class="value-box no-background" type="text" [formControlName]="field.key" />
                          </ng-template>

                          <div
                            class="error-message"
                            *ngIf="
                              metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls[field.key].errors?.['required']
                            "
                          >
                            <span>{{ "common.required" | translate }}</span>
                          </div>
                          <div
                            class="error-message"
                            *ngIf="
                              metadataForm.controls.tumor.controls.tumorCharacteristicsAndHistology.controls[field.key].errors?.[
                                'mustBeEmpty'
                              ]
                            "
                          >
                            <span>{{ "metadataPage.tumor.tumorCharacteristicsAndHistology.mustBeEmpty" | translate }}</span>
                          </div>
                        </ng-template>
                      </div>
                    </ng-template>
                  </li>
                </ul>
              </div>
            </div>

            <div class="field" formGroupName="staging">
              <div class="label">{{ "metadataPage.tumor.staging.title" | translate }}</div>
              <div class="value-box">
                <div class="value-grid-three">
                  <div class="input-with-prefix" *ngFor="let field of stagingFields">
                    <span>{{ "metadataPage.tumor.staging." + field | translate }}<ng-container *ngIf="editMode">*</ng-container>:</span>
                    <ng-container *ngIf="!editMode; else edit">
                      {{ sampleMetadata.tumor.staging[field] || "-" }}
                    </ng-container>

                    <ng-template #edit>
                      <div class="field-edit">
                        <select class="value-box no-background" [formControlName]="field">
                          <option *ngFor="let opt of metadataOptions?.[field]" [ngValue]="opt">{{ opt }}</option>
                        </select>
                      </div>
                      <div class="error-message" *ngIf="metadataForm.controls.tumor.controls.staging.controls[field].errors?.['required']">
                        <span>{{ "common.required" | translate }} </span>
                      </div>
                    </ng-template>
                  </div>

                  <div class="input-with-prefix">
                    <span>{{ "metadataPage.tumor.staging.pathologicalStage" | translate }}:</span>
                    <ng-container *ngIf="!editMode; else editPathologicalStage">
                      {{ sampleMetadata.tumor.staging.pathologicalStage || "-" }}
                    </ng-container>

                    <ng-template #editPathologicalStage>
                      <div class="field-edit">
                        <select class="value-box no-background" formControlName="pathologicalStage">
                          <option
                            *ngFor="let opt of getPathologicalStageOptions(sampleMetadata.tumor.staging.pathologicalStage)"
                            [ngValue]="opt.value"
                          >
                            {{ opt.translate ? (opt.label | translate) : opt.label }}
                          </option>
                        </select>
                      </div>
                    </ng-template>
                  </div>
                </div>
              </div>
            </div>

            <div class="field" formGroupName="diagnostic">
              <div class="label">{{ "metadataPage.tumor.diagnostic.title" | translate }}</div>

              <div class="value-box">
                <ul class="value-grid-two">
                  <li *ngFor="let field of diagnosticFields">
                    <app-multiple-fields
                      [formControlName]="field"
                      [label]="'metadataPage.tumor.diagnostic.' + field | translate"
                      [editMode]="editMode"
                      [isInput]="false"
                      [options]="getBooleanOptions(sampleMetadata.tumor.diagnostic[field])"
                    >
                      {{ sampleMetadata.tumor.diagnostic[field] | booleanLabel | translate }}
                    </app-multiple-fields>
                  </li>
                </ul>
              </div>
            </div>

            <div class="field" formGroupName="metastasis">
              <div class="label">
                {{ "metadataPage.tumor.metastasis.title" | translate }}
              </div>

              <div class="value-box">
                <ul class="value-grid-three">
                  <li *ngFor="let field of metastasisFields">
                    <app-multiple-fields
                      [formControlName]="field"
                      [label]="'metadataPage.tumor.metastasis.' + field | translate"
                      [editMode]="editMode"
                      [isInput]="false"
                      [options]="getBooleanOptions(sampleMetadata.tumor.metastasis[field])"
                    >
                      {{ sampleMetadata.tumor.metastasis[field] | booleanLabel | translate }}
                    </app-multiple-fields>
                  </li>
                </ul>
              </div>
            </div>

            <div class="field" formGroupName="mutation">
              <div class="label">
                {{ "metadataPage.tumor.mutation.title" | translate }}
              </div>

              <div class="value-box">
                <ul class="value-grid-two">
                  <li *ngFor="let field of mutationFields">
                    <app-multiple-fields
                      [formControlName]="field"
                      [label]="'metadataPage.tumor.mutation.' + field | translate"
                      [editMode]="editMode"
                      [isInput]="false"
                      [options]="getBooleanOptions(sampleMetadata.tumor.mutation[field])"
                    >
                      {{ sampleMetadata.tumor.mutation[field] | booleanLabel | translate }}
                    </app-multiple-fields>
                  </li>
                  <li>
                    <app-multiple-fields
                      formControlName="msiStatus"
                      [label]="'metadataPage.tumor.mutation.MSIStatus' | translate"
                      [editMode]="editMode"
                      [isInput]="false"
                      [options]="getMsiStatusOptions(sampleMetadata.tumor.mutation.msiStatus)"
                    >
                      <span>
                        {{ sampleMetadata.tumor.mutation.msiStatus ?? ("common.unknown" | translate) }}
                      </span>
                    </app-multiple-fields>
                  </li>
                </ul>
              </div>
            </div>

            <div class="field" formGroupName="stroma">
              <div class="label">
                {{ "metadataPage.tumor.stroma.title" | translate }}
              </div>

              <div class="value-box">
                <app-multiple-fields
                  formControlName="inflammatoryStroma"
                  [label]="'metadataPage.tumor.stroma.inflammatoryStroma' | translate"
                  [editMode]="editMode"
                  [isInput]="false"
                  [options]="getBooleanOptions(sampleMetadata.tumor.stroma.inflammatoryStroma)"
                >
                  <span>
                    {{ sampleMetadata.tumor.stroma.inflammatoryStroma | booleanLabel | translate }}
                  </span>
                </app-multiple-fields>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TREATMENT DATA -->
      <div class="card" formGroupName="treatment">
        <div class="card-header">
          <div class="card-header-left">
            <lucide-angular name="pill" [size]="20" class="card-icon card-icon-green"></lucide-angular>
            <h3 class="header-title">{{ "metadataPage.treatment.header" | translate }}</h3>
          </div>
        </div>

        <div class="card-content">
          <div class="three-column-card">
            <div>
              <h4 class="card-subheader">
                <lucide-angular name="scissors" [size]="18" class="card-icon card-icon-blue"></lucide-angular>
                {{ "metadataPage.treatment.surgery" | translate }}
              </h4>

              <div class="fields-group">
                <app-single-field
                  formControlName="surgery"
                  [label]="'metadataPage.treatment.surgeryPerformed' | translate"
                  [displayValue]="sampleMetadata.treatment.surgery || '-'"
                  [editMode]="editMode"
                  [isInput]="true"
                ></app-single-field>

                <app-single-field
                  formControlName="description"
                  [label]="'metadataPage.treatment.description' | translate"
                  [displayValue]="sampleMetadata.treatment.description || '-'"
                  [editMode]="editMode"
                  [isInput]="true"
                ></app-single-field>
              </div>
            </div>

            <div formGroupName="beforeSurgery">
              <h4 class="card-subheader">
                <lucide-angular name="clock" [size]="18" class="card-icon card-icon-orange"></lucide-angular>
                {{ "metadataPage.treatment.beforeSurgery" | translate }}
              </h4>

              <div class="fields-group">
                <app-single-field
                  *ngFor="let field of beforeSurgeryFields"
                  [formControlName]="field.key"
                  [label]="'metadataPage.treatment.' + field.key | translate"
                  [displayValue]="
                    field.type === 'boolean'
                      ? ($any(sampleMetadata.treatment.beforeSurgery[field.key]) | booleanLabel | translate)
                      : sampleMetadata.treatment.beforeSurgery[field.key] || '-'
                  "
                  [editMode]="editMode"
                  [isInput]="field.type === 'text'"
                  [options]="getBooleanOptions($any(sampleMetadata.treatment.beforeSurgery[field.key]))"
                  [required]="field.required"
                  [requiredError]="
                    field.required ? metadataForm.controls.treatment.controls.beforeSurgery.controls[field.key].errors?.['required'] : false
                  "
                ></app-single-field>
              </div>
            </div>

            <div formGroupName="afterSurgery">
              <h4 class="card-subheader">
                <lucide-angular name="clock-plus" [size]="18" class="card-icon card-icon-orange"></lucide-angular>
                {{ "metadataPage.treatment.afterSurgery" | translate }}
              </h4>

              <div class="fields-group">
                <app-single-field
                  *ngFor="let field of afterSurgeryFields"
                  [formControlName]="field.key"
                  [label]="'metadataPage.treatment.' + field.key | translate"
                  [displayValue]="
                    field.type === 'boolean'
                      ? ($any(sampleMetadata.treatment.afterSurgery[field.key]) | booleanLabel | translate)
                      : sampleMetadata.treatment.afterSurgery[field.key] || '-'
                  "
                  [editMode]="editMode"
                  [isInput]="field.type === 'text'"
                  [options]="getBooleanOptions($any(sampleMetadata.treatment.afterSurgery[field.key]))"
                  [required]="field.required"
                  [requiredError]="
                    field.required ? metadataForm.controls.treatment.controls.afterSurgery.controls[field.key].errors?.['required'] : false
                  "
                ></app-single-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </form>
</div>
